import React from 'react';
import { Gamepad2, Users, Clock, TrendingUp, Globe, Activity } from 'lucide-react';
import { useGA4 } from '../../contexts/GA4Context';
import { useAdmin } from '../../contexts/AdminContext';
import FirebaseTimeRangeSelector from '../../components/analytics/FirebaseTimeRangeSelector';
import TimeRangeSelector from '../../components/analytics/TimeRangeSelector';
import { formatNumber, formatDuration } from '../../utils/analytics-helpers';
import './StatsOverview.css';

const StatsOverview = ({ analytics }) => {
  const { metrics: ga4Metrics } = useGA4();
  const { dateRange } = useAdmin();

  const {
    totalGames,
    totalPlayers,
    daysInRange,
    gamesPerDay,
    avgCompletionTime,
    avgMoves
  } = analytics;

  // Get readable range label
  const getRangeLabel = () => {
    const presets = {
      'today': 'today',
      'yesterday': 'yesterday',
      '7daysAgo': 'in last 7 days',
      '30daysAgo': 'in last 30 days',
      '90daysAgo': 'in last 90 days',
      'thisMonthStart': 'this month',
      'lastMonthStart': 'last month',
      'thisYearStart': 'this year'
    };
    return presets[dateRange.startDate] || 'in selected range';
  };

  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const stats = [
    {
      label: 'Total Games',
      value: totalGames.toLocaleString(),
      subtext: getRangeLabel(),
      icon: Gamepad2
    },
    {
      label: 'Named Players',
      value: totalPlayers.toLocaleString(),
      subtext: 'Leaderboard submissions',
      icon: Users
    },
    {
      label: 'Avg Completion Time',
      value: formatTime(avgCompletionTime),
      subtext: `${avgMoves.toFixed(1)} avg moves`,
      icon: Clock
    },
    {
      label: 'Games Per Day',
      value: gamesPerDay,
      subtext: `over ${daysInRange} day${daysInRange !== 1 ? 's' : ''}`,
      icon: TrendingUp
    }
  ];

  // GA4 Stats
  const ga4Stats = [
    {
      label: 'Total Sessions',
      value: formatNumber(ga4Metrics?.sessions || 0),
      subtext: 'GA4 visits',
      icon: Globe,
      trend: ga4Metrics?.previousSessions
        ? ((ga4Metrics.sessions - ga4Metrics.previousSessions) / ga4Metrics.previousSessions * 100).toFixed(1)
        : null
    },
    {
      label: 'Active Users',
      value: formatNumber(ga4Metrics?.activeUsers || 0),
      subtext: 'Current period',
      icon: Activity,
      trend: ga4Metrics?.previousActiveUsers
        ? ((ga4Metrics.activeUsers - ga4Metrics.previousActiveUsers) / ga4Metrics.previousActiveUsers * 100).toFixed(1)
        : null
    }
  ];

  return (
    <div className="stats-overview">
      <div className="overview-header">
        <h2>Key Metrics</h2>
        <FirebaseTimeRangeSelector />
      </div>

      <h3 className="section-title">Game Analytics</h3>
      <div className="stats-grid">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="stat-card">
              <div className="stat-header">
                <div className="stat-icon">
                  <Icon size={20} />
                </div>
              </div>
              <div className="stat-content">
                <div className="stat-label">{stat.label}</div>
                <div className="stat-value">{stat.value}</div>
                <div className="stat-subtext">{stat.subtext}</div>
              </div>
            </div>
          );
        })}
      </div>

      <h3 className="section-title">Website Traffic (GA4)</h3>
      <div className="stats-grid">
        {ga4Stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="stat-card">
              <div className="stat-header">
                <div className="stat-icon">
                  <Icon size={20} />
                </div>
              </div>
              <div className="stat-content">
                <div className="stat-label">{stat.label}</div>
                <div className="stat-value">{stat.value}</div>
                <div className="stat-subtext">
                  {stat.trend && (
                    <span className={`trend ${parseFloat(stat.trend) >= 0 ? 'positive' : 'negative'}`}>
                      {parseFloat(stat.trend) >= 0 ? '↑' : '↓'} {Math.abs(stat.trend)}%
                    </span>
                  )}
                  {stat.subtext}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StatsOverview;
